<template>
    <section
      :class="[
        'wrapper',
        'content',
        'content--centered',
        $arMediaQuery.pageContent.maxWidth('sm') && 'sm-max',
        $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
      ]">
      <am2-step-link
        class="step-back-link"
        text="Back to integrations"
        @click="handleBackLinkClick" />
      <am2-opt-in-settings-modal
        :is-show="showOptinSettingsModal"
        @cancel="showOptinSettingsModal = false"
        providerName="Moshtix"
        :message-list-oid="integrationMessageListOid"
      />
      <am2-moshtix-exports-modal
        :is-show="showMoshtixExportsCredentialsModal"
        :loading="isCreatingMoshtixExportsIntegration"
        @confirm="handleMoshtixExportsCredentialsConfirm"
        @cancel="showMoshtixExportsCredentialsModal = false"
      />

      <div
        :class="[
          'title-section',
          $arMediaQuery.pageContent.maxWidth('md') && 'md-max',
        ]">
        <div class="title-section-left">
          <am2-icon-title-section
            title="Moshtix Exports"
            description="Automatically sync your data from Moshtix Exports"
            :icon-props="{
              name: 'moshtix',
              color: $arStyle.color.moshtix,
              height: '46px',
              width: '40px',
            }"
          />
        </div>
        <div class="title-section-right" v-if="!isFetchingMoshtixExportsIntegration && integrationsSummary">
          <am2-integration-account-select
            v-if="!isFetchingMoshtixExportsIntegration && moshtixExportsIntegration && currentIntegration"
            :default-selected-integration-oid="currentIntegration.oid"
            :available-integrations="integrationsSummary"
            @select="handleIntegrationChange"
            @new-account="handleAddNewIntegration"
            :style="{ marginRight: '10px', maxWidth: 'calc(100vw - 80px)', }"
          />
          <am2-expand-button-dropdown
            align="left"
            :button-props="{ iconName: 'settings' }"
            :items="expandButtonItems"
            @select="handleSettingOptionSelect" />
        </div>
      </div>

      <am2-tabs
        :items="[
          { title: 'Notifications' },
          { title: 'Status' }
        ]"
        :selected-tab-index="selectedTabIndex"
        class="tab-options"
        @select="(selected, idx) => selectedTabIndex = idx"
      />
      <div
        v-if="selectedTabIndex === 0"
      >
        <NotificationsContainer
          :integration="currentIntegration"
          :loading="isFetchingMoshtixExportsIntegration"
        />
      </div>
      <div
        v-if="selectedTabIndex === 1"
        class="integration-status-container"
      >
      <section v-if="isCreatingMoshtixExportsIntegration || isFetchingMoshtixExportsIntegration" class="integration-loading-container">
        <am2-loading-bubble />
      </section>
      <section v-else>
        <section v-if="integrationFailed" class="integration-failed-message">
          <ar-snackbar
            type="warning"
            message="Your Moshtix Exports connection is no longer valid. This means we can't sync your member data. <a>Please reconnect your account</a>"
            @anchorClick="handleReconnectIntegration"
            :style="{ width: '100%' }"
          />
        </section>

        <SyncBlockContainer
          v-else
          class="sync-block-container"
          :is-fetching-sync-task="isFetchingSyncTask"
          :sync-status="syncStatus"
          :sync-time="syncTime"
          :sync-copies="{
            'in-progress': 'Syncing data from Moshtix Exports',
            'pending': 'The data sync is still pending',
            'completed': 'Your Moshtix Exports account is currently connected',
            'cancelled': 'Your Moshtix Exports account connection is cancelled',
            'failed': this.syncErrorMessage,
            'pending-setup': 'Please contact Audience Republic via support@audiencerepublic.com to finish connecting your Moshtix Exports account.',
            'default': 'Syncing tasks not found',
          }"
          @startSync="handleStartSync"
          @stopSync="handleStopSync"
        />
      </section>
      </div>

    </section>
  </template>

  <script>
  import { mapState, mapActions, mapMutations } from 'vuex';
  import SyncBlockContainer from '../components/sync-block-container';
  import NotificationsContainer from '../components/notifications-container';

  export default {
    name: 'MoshtixExportsIntegration',
    layout: 'default',
    components: {
      SyncBlockContainer,
      NotificationsContainer
    },

    data: () => ({
      currentlySelectedIntegrationIndex: 0,
      syncStatusPoller: null,
      showMoshtixExportsCredentialsModal: false,
      showOptinSettingsModal: false,
      selectedTabIndex: 1
    }),

    computed: {
      ...mapState({
        moshtixExportsIntegration: state => state.moshtixExportsIntegration.integration,
        isFetchingMoshtixExportsIntegration: state => state.moshtixExportsIntegration.isFetchingIntegration,
        isAddingMoshtixExportsIntegration: state => state.moshtixExportsIntegration.isAddingIntegration,
        isCreatingMoshtixExportsIntegration: state => state.moshtixExportsIntegration.isCreatingIntegration,
        isDeletingMoshtixExportsIntegration: state => state.moshtixExportsIntegration.isDeletingIntegration,
        isFetchingSyncTask: state => state.moshtixExportsIntegration.isFetchingSyncTask,
        syncTask: state => state.moshtixExportsIntegration.syncTask,
        syncStatus: state => state.moshtixExportsIntegration.syncStatus,
      }),

      moshtixExportsIntegrationEnabled() {
        return true;
      },

      syncErrorMessage() {
        return this.syncTask?.statusDetails?.lastError || this.syncTask?.statusDetails?.reason;
      },

      syncTime() {
        return this.syncTask?.sysMtime || null;
      },

      integrationsSummary() {
        if (!this.moshtixExportsIntegration) return null;
        const summary = this.moshtixExportsIntegration
          .map(i => ({
            oid: i.oid,
            name: i.integration.accountName || 'Default Account',
            email: i.accountId,
            mailingListOid: i.integration?.mailingListOid
          }));

        return summary
      },

      currentIntegration() {
        if (this.currentlySelectedIntegrationIndex === null) return null;
        if (!this.moshtixExportsIntegration || this.moshtixExportsIntegration.length < 1 || !this.moshtixExportsIntegration[this.currentlySelectedIntegrationIndex]) return null;
        return this.moshtixExportsIntegration[this.currentlySelectedIntegrationIndex];
      },

      integrationMessageListOid() {
        return this.currentIntegration ? this.currentIntegration?.meta?.messageListOid : null;
      },

      expandButtonItems() {
        let i = [];
        i.push({
          name: 'Moshtix Exports Settings',
          value: 'openSettingsModal'
        });
        i.push({
          name: 'Reconnect Integration',
          value: 'reconnectIntegration'
        });
        i.push({
          name: 'Remove Account',
          value: 'removeIntegration'
        });
        return i;
      },

      integrationFailed() {
        if (!this.currentIntegration) return false;
        return this.currentIntegration.status === 'failed';
      },
    },

    async mounted() {
      if (!this.moshtixExportsIntegrationEnabled) {
        this.handleBackLinkClick();
      }
      await this.fetchIntegration();
      this.startFetchTasksPolling();
    },

    beforeDestroy() {
      this['moshtixExportsIntegration/RESET_INTEGRATION']();

      if (this.syncStatusPoller) {
        clearInterval(this.syncStatusPoller);
      }
    },

    methods: {
      ...mapActions([
        'SHOW_CONFIRM',
        'moshtixExportsIntegration/FETCH_INTEGRATION',
        'moshtixExportsIntegration/DELETE_INTEGRATION',
        'moshtixExportsIntegration/SYNC_START',
        'moshtixExportsIntegration/SYNC_STOP',
        'moshtixExportsIntegration/FETCH_SYNC_TASK',
        'moshtixExportsIntegration/CREATE_MOSHTIX_EXPORTS_INTEGRATION',
        'promoterTasks/START_POLLING_PENDING_TASKS',
      ]),
      ...mapMutations([
        'moshtixExportsIntegration/RESET_INTEGRATION',
      ]),
      startFetchTasksPolling() {
        if (this.currentIntegration?.oid && !this.isFetchingSyncTask) {
          this.fetchTasks();
          this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
        }
      },
      async fetchIntegration() {
        await this['moshtixExportsIntegration/FETCH_INTEGRATION']();
      },

      async fetchTasks() {
        await this['moshtixExportsIntegration/FETCH_SYNC_TASK'](this.currentIntegration.oid);

        if (this.currentIntegration && this.hasExistingTask && this.syncStatus !== "in-progress") {
          window.clearInterval(this.syncStatusPoller);
          this.syncStatusPoller = null;
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }
      },

      async handleMoshtixExportsCredentialsConfirm({ providerName }) {
        const succeed = await this['moshtixExportsIntegration/CREATE_MOSHTIX_EXPORTS_INTEGRATION']({
            providerName
        });
        if (succeed) {
          this.fetchIntegration();

          this.showMoshtixExportsCredentialsModal = false;
        }
      },


      hasExistingTask() {
        return this.syncTask !== null;
      },

      handleBackLinkClick() {
        this.$router.push({ path: '/settings/integrations' });
      },

      handleIntegrationChange(integration, index) {
        this.currentlySelectedIntegrationIndex = index;
        this.fetchTasks();
      },

      async handleAddNewIntegration() {
        this.showMoshtixExportsCredentialsModal = true;
      },

      async handleStartSync() {
        setTimeout(() => {
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }, 750);
        await this['moshtixExportsIntegration/SYNC_START']({ oid: this.currentIntegration.oid });
        if (this.syncStatus !== 'completed') {
          this.syncStatusPoller = setInterval(this.fetchTasks, 3000);
        }
      },

      handleStopSync() {
        this['moshtixExportsIntegration/SYNC_STOP']({oid: this.currentIntegration.oid});
        setTimeout(() => {
          this['promoterTasks/START_POLLING_PENDING_TASKS']({});
        }, 750);
      },

      async handleReconnectIntegration() {
        const agreed = await this.SHOW_CONFIRM({
          messageHtml: `Make sure you are logging in with the Moshtix Exports account <strong>${this.currentIntegration.accountId}</strong> on Moshtix Exports before reconnecting it.`,
          confirmButtonText: 'Reconnect account',
        });
        if (!agreed) {
          return;
        }

        const succeed = await this['moshtixExportsIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
        if (!succeed) {
          return;
        }

        this.handleAddNewIntegration();
      },

      async handleSettingOptionSelect(item) {
        if (item.value === 'removeIntegration') {
          const answer = await this.SHOW_CONFIRM({
            messageHtml: 'Removing the Moshtix Exports integration will stop syncing your data from Moshtix Exports.',
            confirmButtonText: 'Remove integration',
            iconName: 'alert-question-mark',
            title: 'Are you sure?',
          });
          if (answer) {
            try {
              await this['moshtixExportsIntegration/DELETE_INTEGRATION'](this.currentIntegration.oid);
              if (this.moshtixExportsIntegration.length > 0) {
                this.currentlySelectedIntegrationIndex = 0;
                await this.fetchIntegration();
                this.startFetchTasksPolling();
              } else {
                this.$router.push({ path: '/settings/integrations' });
              }
            } catch (e) {
              this.$arNotification.push({ type: 'error', message: 'Failed to remove integration' });
            }
          }
        } else if (item.value === 'reconnectIntegration') {
          this.handleReconnectIntegration();
        } else if (item.value == 'openSettingsModal') {
          if (!this.currentIntegration) return;
          this.showOptinSettingsModal = true;
        }
      },
    }
  };
  </script>

  <style lang="scss" scoped>
  .wrapper {

    .tab-options {
      margin: 60px 0 20px 0;
      border-bottom: 1px $skyBlueGrey500 solid;
    }

    &.sm-max {
      padding: 0 24px;
    }
    &.xs-max {
      padding: 0 12px;
    }

    .integration-loading-container {
      display: flex;
      justify-content: center;
      margin-top: 65px;
    }


    .step-back-link {
      margin-top: 48px;
    }

    .title-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 48px;

      .title-section-left {
        display: inline-flex;
        align-items: flex-start;

        .tag {
          position: relative;
          margin-left: 10px;
          top: 4px;
        }
      }

      .title-section-right {
        display: inline-flex;
        align-items: center;

        .select {
          width: 300px;
          margin-right: 10px;
        }
      }

      &.md-max {
        flex-direction: column;
        align-items: flex-start;

        .title-section-right {
          margin-top:16px;
        }
      }
    }


    .sync-block-container {
      margin-top: 0px;
    }
  }
  </style>
